import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Input } from './ui/Input';
import { Button } from './ui/Button';
import { Alert } from './ui/Alert';
import { apiFetch, parseJsonResponse, isNetworkError, isNetworkErrorResponse } from '../utils/api';

interface Client {
  id: number;
  account_name: string;
  telephone: string | null;
  code_client_license: number;
  email_license: string;
  export_id_hioffice: number;
  export_id_comments: number | null;
  dir_cloudclient: string | null;
  port_cloudclient: number | null;
  is_active: number;
}

interface ClientFormProps {
  client?: Client | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export const ClientForm: React.FC<ClientFormProps> = ({ client, onSuccess, onCancel }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    account_name: '',
    telephone: '',
    code_client_license: '',
    email_license: '',
    password_license: '',
    export_id_hioffice: '',
    export_id_comments: '',
    dir_cloudclient: '',
    port_cloudclient: '',
    is_active: true
  });

  useEffect(() => {
    if (client) {
      setFormData({
        account_name: client.account_name || '',
        telephone: client.telephone || '',
        code_client_license: client.code_client_license?.toString() || '',
        email_license: client.email_license || '',
        password_license: '', // No mostrar contraseña existente
        export_id_hioffice: client.export_id_hioffice?.toString() || '',
        export_id_comments: client.export_id_comments?.toString() || '',
        dir_cloudclient: client.dir_cloudclient || '',
        port_cloudclient: client.port_cloudclient?.toString() || '',
        is_active: client.is_active === 1
      });
    }
  }, [client]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setError('No hay token de autenticación');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const isEdit = !!client;
      const route = isEdit ? 'clients/update' : 'clients/create';

      const payload: any = {
        account_name: formData.account_name,
        telephone: formData.telephone || null,
        code_client_license: parseInt(formData.code_client_license),
        email_license: formData.email_license,
        export_id_hioffice: parseInt(formData.export_id_hioffice),
        export_id_comments: formData.export_id_comments ? parseInt(formData.export_id_comments) : null,
        dir_cloudclient: formData.dir_cloudclient || null,
        port_cloudclient: formData.port_cloudclient ? parseInt(formData.port_cloudclient) : null,
        is_active: formData.is_active ? 1 : 0
      };

      // Solo incluir contraseña si se está creando o si se está editando y se proporciona una nueva
      if (!isEdit) {
        payload.password_license = formData.password_license;
      } else {
        payload.id = client.id;
        if (formData.password_license) {
          payload.password_license = formData.password_license;
        }
      }

      const res = await apiFetch(route, {
        method: 'POST',
        body: JSON.stringify(payload)
      }, token);

      // Si es error de red, mostrar mensaje y mantener el formulario abierto
      if (isNetworkErrorResponse(res)) {
        setError('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
        return;
      }

      const json = await parseJsonResponse(res);

      if (!res.ok || json.error) {
        throw new Error(json.error || json.message || json.data?.error || 'Error al guardar cliente');
      }

      onSuccess();
    } catch (err: any) {
      if (isNetworkError(err)) {
        setError('Error de conexión. Verifica tu conexión a internet e intenta de nuevo.');
      } else {
      setError(err.message || 'Error inesperado');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {error && (
        <div className="mb-6">
          <Alert variant="error">{error}</Alert>
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              type="text"
              label="Nombre Comercial"
              name="account_name"
              value={formData.account_name}
              onChange={handleChange}
              required
              placeholder="Ej: Restaurante El Buen Sabor"
            />

            <Input
              type="tel"
              label="Teléfono"
              name="telephone"
              value={formData.telephone}
              onChange={handleChange}
              placeholder="Ej: +52 555 123 4567"
            />

            <Input
              type="number"
              label="Código de Licencia"
              name="code_client_license"
              value={formData.code_client_license}
              onChange={handleChange}
              required
              placeholder="12345"
            />

            <Input
              type="email"
              label="Email de Licencia"
              name="email_license"
              value={formData.email_license}
              onChange={handleChange}
              required
              placeholder="cliente@ejemplo.com"
            />

            <Input
              type="password"
              label={client ? 'Nueva Contraseña (opcional)' : 'Contraseña'}
              name="password_license"
              value={formData.password_license}
              onChange={handleChange}
              required={!client}
              helperText={client ? 'Deja vacío para mantener la contraseña actual' : ''}
              placeholder="••••••••"
            />

            <Input
              type="number"
              label="ID de Exportación Hioffice"
              name="export_id_hioffice"
              value={formData.export_id_hioffice}
              onChange={handleChange}
              required
              placeholder="12345"
            />

            <Input
              type="number"
              label="ID Exportación Comentarios"
              name="export_id_comments"
              value={formData.export_id_comments}
              onChange={handleChange}
              placeholder="12345"
              helperText="ID de exportación para sincronizar comentarios desde Hioffice"
            />

            <Input
              type="url"
              label="URL Cliente en la Nube"
              name="dir_cloudclient"
              value={formData.dir_cloudclient}
              onChange={handleChange}
              placeholder="https://cliente.ejemplo.com"
            />

            <Input
              type="number"
              label="Puerto del Cliente"
              name="port_cloudclient"
              value={formData.port_cloudclient}
              onChange={handleChange}
              placeholder="8080"
            />

            {/* Checkbox para estado activo */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
                />
                <div>
                  <span className="text-sm font-semibold text-gray-900">Cliente activo</span>
                  <p className="text-xs text-gray-500 mt-0.5">Los clientes inactivos no podrán acceder al sistema</p>
                </div>
              </label>
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex flex-col sm:flex-row justify-end gap-3 pt-6 mt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              size="md"
              onClick={onCancel}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="md"
              isLoading={loading}
            >
              {client ? 'Actualizar Cliente' : 'Crear Cliente'}
            </Button>
          </div>
      </form>
    </div>
  );
};
